import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { dashboardApi } from '../api/dashboard.api';
import { analyticsApi } from '../api/analytics.api';
import { getBizuImageUrl, getProductImageUrl, getRecipeImageUrl } from '../utils/images';
import {
  UsersIcon,
  UserPlusIcon,
  ClockIcon,
  CursorArrowRaysIcon,
  ChartBarIcon,
  BookOpenIcon,
  Square3Stack3DIcon,
  FireIcon,
  SparklesIcon,
  TrophyIcon,
  CurrencyDollarIcon,
  BoltIcon,
  CheckCircleIcon,
  MicrophoneIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';

function StatCard({ title, value, subtitle, icon: Icon, color, to, onClick }) {
  const content = (
    <div className="flex items-center">
      <div className={`p-3 rounded-lg ${color}`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
      <div className="ml-4">
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
      </div>
    </div>
  );

  if (to) {
    return (
      <Link to={to} className="block bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow">
        {content}
      </Link>
    );
  }

  if (onClick) {
    return (
      <button onClick={onClick} className="block w-full text-left bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow cursor-pointer">
        {content}
      </button>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {content}
    </div>
  );
}

function TopUserCard({ user, rank }) {
  const formatActiveTime = (ms) => {
    if (!ms) return '0m';
    const hours = Math.floor(ms / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  return (
    <Link
      to={`/users/${user._id}`}
      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
    >
      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
        rank === 1 ? 'bg-yellow-500' : rank === 2 ? 'bg-gray-400' : rank === 3 ? 'bg-amber-600' : 'bg-gray-300'
      }`}>
        {rank}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-900 truncate">{user.fullName}</p>
        <p className="text-xs text-gray-500">@{user.username}</p>
      </div>
      <div className="text-right">
        <p className="text-sm font-semibold text-gray-900">{user.pageVisits}</p>
        <p className="text-xs text-gray-500">{formatActiveTime(user.activeTimeMs)}</p>
      </div>
    </Link>
  );
}

// Modal component
function Modal({ isOpen, onClose, title, children }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
              <XMarkIcon className="h-5 w-5 text-gray-500" />
            </button>
          </div>
          <div className="p-4 overflow-y-auto max-h-[calc(80vh-60px)]">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [modalType, setModalType] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsResponse, analyticsResponse] = await Promise.all([
          dashboardApi.getStats(),
          analyticsApi.getOverview(30).catch(() => ({ data: null }))
        ]);
        setStats(statsResponse.data);
        setAnalytics(analyticsResponse.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatActiveTime = (ms) => {
    if (!ms) return '0h';
    const hours = Math.floor(ms / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const openModal = async (type) => {
    setModalType(type);
    setModalLoading(true);
    try {
      let response;
      switch (type) {
        case 'newUsersWeek':
          response = await dashboardApi.getNewUsersThisWeek();
          break;
        case 'activeToday':
          response = await dashboardApi.getActiveUsersToday();
          break;
        case 'activeWeek':
          response = await dashboardApi.getActiveUsersWeek();
          break;
        case 'loginsWeek':
          response = await dashboardApi.getLoginsThisWeek();
          break;
        case 'pageVisits':
          response = await dashboardApi.getPageVisits();
          break;
        case 'aiActiveUsers':
          response = await dashboardApi.getAIActiveUsers();
          break;
        default:
          break;
      }
      setModalData(response?.data);
    } catch (error) {
      console.error('Error fetching modal data:', error);
      setModalData(null);
    } finally {
      setModalLoading(false);
    }
  };

  const openProductModal = async (productName, productDisplayName) => {
    setModalType('productUsers');
    setModalLoading(true);
    try {
      const response = await dashboardApi.getProductUsers(productName);
      setModalData({ ...response?.data, productDisplayName });
    } catch (error) {
      console.error('Error fetching product users:', error);
      setModalData(null);
    } finally {
      setModalLoading(false);
    }
  };

  const openRecipeModal = () => {
    setModalType('recipe');
    setModalData(stats?.gameMetrics?.mostPlayedRecipe);
  };

  const closeModal = () => {
    setModalType(null);
    setModalData(null);
  };

  const renderModalContent = () => {
    if (modalLoading) {
      return (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3ee851]"></div>
        </div>
      );
    }

    if (!modalData) {
      return <p className="text-center text-gray-500 py-4">No data available</p>;
    }

    switch (modalType) {
      case 'newUsersWeek':
        return (
          <div className="space-y-2">
            {modalData.users?.map(user => (
              <Link key={user._id} to={`/users/${user._id}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                <div>
                  <p className="font-medium text-gray-900">{user.fullName}</p>
                  <p className="text-xs text-gray-500">@{user.username}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">{new Date(user.createdAt).toLocaleDateString()}</p>
                </div>
              </Link>
            ))}
            {(!modalData.users || modalData.users.length === 0) && (
              <p className="text-center text-gray-500 py-4">No new users this week</p>
            )}
          </div>
        );

      case 'activeToday':
      case 'activeWeek':
        return (
          <div className="space-y-2">
            {modalData.users?.map(user => (
              <Link key={user._id} to={`/users/${user._id}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                <div>
                  <p className="font-medium text-gray-900">{user.fullName}</p>
                  <p className="text-xs text-gray-500">@{user.username}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900">{user.pageVisits} visits</p>
                  <p className="text-xs text-gray-500">{formatActiveTime(user.activeTimeMs)}</p>
                </div>
              </Link>
            ))}
            {(!modalData.users || modalData.users.length === 0) && (
              <p className="text-center text-gray-500 py-4">No active users</p>
            )}
          </div>
        );

      case 'loginsWeek':
        return (
          <div className="space-y-2">
            <div className="flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
              <span>User</span>
              <span>Total Logins</span>
            </div>
            {modalData.users?.map(user => (
              <Link key={user._id} to={`/users/${user._id}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                <div>
                  <p className="font-medium text-gray-900">{user.fullName}</p>
                  <p className="text-xs text-gray-500">@{user.username}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-gray-900">{user.totalLogins}</p>
                </div>
              </Link>
            ))}
            {(!modalData.users || modalData.users.length === 0) && (
              <p className="text-center text-gray-500 py-4">No logins this week</p>
            )}
          </div>
        );

      case 'pageVisits':
        return (
          <div className="space-y-2">
            <div className="flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
              <span>Page</span>
              <span>Visits</span>
            </div>
            {modalData.pages?.map((page, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <p className="font-medium text-gray-900 truncate mr-4">{page.page}</p>
                <p className="text-lg font-bold text-gray-900 whitespace-nowrap">{page.visits}</p>
              </div>
            ))}
            {(!modalData.pages || modalData.pages.length === 0) && (
              <p className="text-center text-gray-500 py-4">No page visit data</p>
            )}
          </div>
        );

      case 'aiActiveUsers':
        return (
          <div className="space-y-2">
            <div className="flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
              <span>User</span>
              <span>Requests / Cost</span>
            </div>
            {modalData.users?.map(user => (
              <Link key={user._id} to={`/users/${user._id}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                <div>
                  <p className="font-medium text-gray-900">{user.fullName}</p>
                  <p className="text-xs text-gray-500">@{user.username}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900">{user.totalRequests} requests</p>
                  <p className="text-xs text-gray-500">${(user.totalCost || 0).toFixed(4)}</p>
                </div>
              </Link>
            ))}
            {(!modalData.users || modalData.users.length === 0) && (
              <p className="text-center text-gray-500 py-4">No AI active users</p>
            )}
          </div>
        );

      case 'recipe':
        return modalData ? (
          <div>
            <div className="flex flex-col items-center mb-6">
              {modalData.image && (
                <img
                  src={getRecipeImageUrl(modalData.image)}
                  alt={modalData.displayName}
                  className="w-48 h-48 object-cover rounded-lg mb-4"
                />
              )}
              <h4 className="text-xl font-bold text-gray-900">{modalData.displayName}</h4>
              {modalData.name_es && (
                <p className="text-sm text-gray-500">{modalData.name_es}</p>
              )}
              <p className="text-sm text-gray-600 mt-2">Played <strong>{modalData.timesPlayed}</strong> times</p>
            </div>
            {modalData.ingredients && modalData.ingredients.length > 0 && (
              <div className="mb-4">
                <h5 className="text-sm font-semibold text-gray-700 mb-2">Ingredients</h5>
                <div className="grid grid-cols-2 gap-2">
                  {modalData.ingredients.map((ing, i) => (
                    <div key={i} className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded">
                      {ing.quantity} {ing.unit} {ing.ingredient_one || ing.ingredient_many}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {modalData._id && (
              <div className="flex justify-center mt-4">
                <Link
                  to={`/recipes/${modalData._id}/ingredients`}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#3ee851] text-white rounded-lg hover:bg-[#35cc47] transition-colors"
                  onClick={closeModal}
                >
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                  View Recipe
                </Link>
              </div>
            )}
          </div>
        ) : null;

      case 'productUsers':
        return (
          <div className="space-y-2">
            <p className="text-sm text-gray-600 mb-3">
              Users who generated <strong>{modalData.productDisplayName}</strong>
            </p>
            {modalData.users?.map(user => (
              <Link key={user._id} to={`/users/${user._id}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                <div>
                  <p className="font-medium text-gray-900">{user.fullName}</p>
                  <p className="text-xs text-gray-500">@{user.username}</p>
                </div>
                {user.generatedAt && (
                  <p className="text-sm text-gray-500">{new Date(user.generatedAt).toLocaleDateString()}</p>
                )}
              </Link>
            ))}
            {(!modalData.users || modalData.users.length === 0) && (
              <p className="text-center text-gray-500 py-4">No users found</p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const getModalTitle = () => {
    switch (modalType) {
      case 'newUsersWeek': return 'New Users This Week';
      case 'activeToday': return 'Active Users Today';
      case 'activeWeek': return 'Active Users This Week';
      case 'loginsWeek': return 'Logins This Week';
      case 'pageVisits': return 'Page Visits';
      case 'aiActiveUsers': return 'AI Active Users';
      case 'recipe': return 'Most Played Recipe';
      case 'productUsers': return 'Product Users';
      default: return '';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3ee851]"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Welcome to Bizumia Admin Dashboard</p>
      </div>

      {/* Overview Stats */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Users"
            value={stats?.overview?.totalUsers || 0}
            icon={UsersIcon}
            color="bg-blue-500"
            to="/users"
          />
          <StatCard
            title="New This Week"
            value={stats?.overview?.newUsersThisWeek || 0}
            subtitle="new users"
            icon={UserPlusIcon}
            color="bg-green-500"
            onClick={() => openModal('newUsersWeek')}
          />
          <StatCard
            title="Total Recipes"
            value={stats?.overview?.totalRecipes || 0}
            icon={BookOpenIcon}
            color="bg-orange-500"
            to="/recipes"
          />
          <StatCard
            title="User Recipes"
            value={stats?.overview?.userCreatedRecipes || 0}
            subtitle="created by users"
            icon={BookOpenIcon}
            color="bg-purple-500"
            to="/recipes"
          />
        </div>
      </div>

      {/* User Activity Stats */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">User Activity</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          <StatCard
            title="Active Today"
            value={stats?.activity?.activeUsersToday || 0}
            subtitle="users"
            icon={FireIcon}
            color="bg-red-500"
            onClick={() => openModal('activeToday')}
          />
          <StatCard
            title="Active This Week"
            value={stats?.activity?.activeUsersWeek || 0}
            subtitle="users"
            icon={ChartBarIcon}
            color="bg-indigo-500"
            onClick={() => openModal('activeWeek')}
          />
          <StatCard
            title="Total Logins"
            value={stats?.activity?.totalLogins || 0}
            icon={UsersIcon}
            color="bg-blue-500"
            onClick={() => openModal('loginsWeek')}
          />
          <StatCard
            title="Page Visits"
            value={stats?.activity?.totalPageVisits || 0}
            icon={CursorArrowRaysIcon}
            color="bg-cyan-500"
            onClick={() => openModal('pageVisits')}
          />
          <StatCard
            title="Active Time"
            value={formatActiveTime(stats?.activity?.totalActiveTimeMs)}
            subtitle="total"
            icon={ClockIcon}
            color="bg-teal-500"
          />
        </div>
      </div>

      {/* Analytics Stats */}
      {analytics && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Analytics</h2>
            <Link to="/analytics" className="text-sm text-primary-600 hover:text-primary-800">
              View Details →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <StatCard
              title="Total Cost"
              value={`$${(analytics.summary?.totalCost || 0).toFixed(2)}`}
              subtitle="last 30 days"
              icon={CurrencyDollarIcon}
              color="bg-emerald-500"
              to="/analytics/costs"
            />
            <StatCard
              title="API Requests"
              value={(analytics.summary?.totalRequests || 0).toLocaleString()}
              subtitle="last 30 days"
              icon={BoltIcon}
              color="bg-amber-500"
              to="/analytics/logs"
            />
            <StatCard
              title="Success Rate"
              value={`${analytics.summary?.successRate || 0}%`}
              icon={CheckCircleIcon}
              color="bg-green-500"
              to="/analytics/logs"
            />
            <StatCard
              title="Active Users"
              value={analytics.summary?.activeUsers || 0}
              subtitle="using AI features"
              icon={UsersIcon}
              color="bg-violet-500"
              onClick={() => openModal('aiActiveUsers')}
            />
            <StatCard
              title="Avg Duration"
              value={`${((analytics.summary?.avgDuration || 0) / 1000).toFixed(1)}s`}
              subtitle="per request"
              icon={ClockIcon}
              color="bg-sky-500"
              to="/analytics/logs"
            />
          </div>
        </div>
      )}

      {/* Top Active Users and Game Metrics Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Top Active Users */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Active Users</h2>
          <div className="bg-white rounded-lg shadow p-4">
            {stats?.topActiveUsers?.length > 0 ? (
              <div className="space-y-2">
                {stats.topActiveUsers.map((user, index) => (
                  <TopUserCard key={user._id} user={user} rank={index + 1} />
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-4">No activity data yet</p>
            )}
          </div>
        </div>

        {/* Game Metrics */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Game Metrics</h2>
          <div className="bg-white rounded-lg shadow p-4 space-y-4">
            {/* Most Played Bizu */}
            {stats?.gameMetrics?.mostPlayedBizu && (
              <Link
                to={`/bizu/${stats.gameMetrics.mostPlayedBizu.name}`}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="h-10 w-10 rounded-lg overflow-hidden bg-gray-100">
                  <img
                    src={getBizuImageUrl(stats.gameMetrics.mostPlayedBizu.name)}
                    alt={stats.gameMetrics.mostPlayedBizu.displayName}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Most Played Bizu</p>
                  <p className="font-semibold text-gray-900">{stats.gameMetrics.mostPlayedBizu.displayName}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-gray-900">{stats.gameMetrics.mostPlayedBizu.timesPlayed}</p>
                  <p className="text-xs text-gray-500">plays</p>
                </div>
              </Link>
            )}

            {/* Most Played Recipe */}
            {stats?.gameMetrics?.mostPlayedRecipe && (
              <button
                onClick={openRecipeModal}
                className="w-full flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-left"
              >
                <div className="h-10 w-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                  {stats.gameMetrics.mostPlayedRecipe.image ? (
                    <img
                      src={getRecipeImageUrl(stats.gameMetrics.mostPlayedRecipe.image)}
                      alt={stats.gameMetrics.mostPlayedRecipe.displayName}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center bg-bizu-kien">
                      <BookOpenIcon className="h-6 w-6 text-white" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Most Played Recipe</p>
                  <p className="font-semibold text-gray-900">{stats.gameMetrics.mostPlayedRecipe.displayName}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-gray-900">{stats.gameMetrics.mostPlayedRecipe.timesPlayed}</p>
                  <p className="text-xs text-gray-500">plays</p>
                </div>
              </button>
            )}

            {/* Most Generated Fruit */}
            {stats?.gameMetrics?.mostGeneratedFruit && (
              <button
                onClick={() => openProductModal(stats.gameMetrics.mostGeneratedFruit.product, stats.gameMetrics.mostGeneratedFruit.name)}
                className="w-full flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-left"
              >
                <div className="h-10 w-10 rounded-lg overflow-hidden bg-red-50 flex-shrink-0">
                  <img
                    src={getProductImageUrl(stats.gameMetrics.mostGeneratedFruit.product)}
                    alt={stats.gameMetrics.mostGeneratedFruit.name}
                    className="h-full w-full object-contain p-1"
                  />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Most Generated Fruit</p>
                  <p className="font-semibold text-gray-900">{stats.gameMetrics.mostGeneratedFruit.name}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-gray-900">{stats.gameMetrics.mostGeneratedFruit.count}</p>
                  <p className="text-xs text-gray-500">times</p>
                </div>
              </button>
            )}

            {/* Most Generated Veggie */}
            {stats?.gameMetrics?.mostGeneratedVeggie && (
              <button
                onClick={() => openProductModal(stats.gameMetrics.mostGeneratedVeggie.product, stats.gameMetrics.mostGeneratedVeggie.name)}
                className="w-full flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-left"
              >
                <div className="h-10 w-10 rounded-lg overflow-hidden bg-green-50 flex-shrink-0">
                  <img
                    src={getProductImageUrl(stats.gameMetrics.mostGeneratedVeggie.product)}
                    alt={stats.gameMetrics.mostGeneratedVeggie.name}
                    className="h-full w-full object-contain p-1"
                  />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Most Generated Veggie</p>
                  <p className="font-semibold text-gray-900">{stats.gameMetrics.mostGeneratedVeggie.name}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-gray-900">{stats.gameMetrics.mostGeneratedVeggie.count}</p>
                  <p className="text-xs text-gray-500">times</p>
                </div>
              </button>
            )}

            {/* Stats Row */}
            <div className="grid grid-cols-2 gap-4 pt-2">
              <div className="text-center p-3 bg-bizu-kien/10 rounded-lg">
                <p className="text-2xl font-bold text-bizu-kien">{stats?.gameMetrics?.userCreatedRecipes || 0}</p>
                <p className="text-xs text-gray-500">User Recipes</p>
              </div>
              <div className="text-center p-3 bg-bizu-queso/10 rounded-lg">
                <p className="text-2xl font-bold text-bizu-queso">{stats?.gameMetrics?.totalQuesoCards || 0}</p>
                <p className="text-xs text-gray-500">Queso Cards</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      <Modal isOpen={!!modalType} onClose={closeModal} title={getModalTitle()}>
        {renderModalContent()}
      </Modal>
    </DashboardLayout>
  );
}
